import { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';

export default function WhatsAppCTA() {
  const [linked, setLinked] = useState(false);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    checkStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/gateway/whatsapp/status');
      setLinked(res.data.linked);
      setPhone(res.data.phoneNumber || '');
    } catch {
      // Not connected
    } finally {
      setChecking(false);
    }
  };

  const generateCode = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/gateway/whatsapp/link');
      setCode(res.data.code);
      // Start polling for connection
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.get('/api/gateway/whatsapp/status');
          if (status.data.linked) {
            setLinked(true);
            setPhone(status.data.phoneNumber || '');
            setCode('');
            if (pollRef.current) clearInterval(pollRef.current);
          }
        } catch {}
      }, 3000);
    } catch {
      console.error('Failed to generate WhatsApp code');
    } finally {
      setLoading(false);
    }
  };

  if (checking) return null;

  // Already connected — show subtle pill
  if (linked) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
        <span className="w-2 h-2 bg-green-500 rounded-full" />
        <span className="font-medium">WhatsApp connected</span>
        {phone && <span className="text-green-600">{phone}</span>}
      </div>
    );
  }

  // Code generated — show instructions
  if (code) {
    const waLink = `https://wa.me/573102197789?text=${encodeURIComponent(`connect ${code}`)}`;
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-xl flex-shrink-0">
            💬
          </div>
          <div className="flex-1">
            <p className="font-medium text-green-900 mb-2">Send this message to activate WhatsApp:</p>
            <code className="block text-lg font-mono font-bold bg-white border border-green-200 px-3 py-2 rounded-lg text-green-800">
              connect {code}
            </code>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Open WhatsApp
              <span>→</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Not connected — show CTA
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-xl">
            💬
          </div>
          <div>
            <p className="font-medium text-green-900">Connect WhatsApp</p>
            <p className="text-xs text-green-700">Chat with your agent anytime, anywhere</p>
          </div>
        </div>
        <button
          onClick={generateCode}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {loading ? '...' : 'Connect'}
        </button>
      </div>
    </div>
  );
}
